import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { useEffect } from 'react'
import { useState } from 'react'
import axios from 'axios'

import { API_BASE_URL } from '../../config';
import StudentAccessManager from './StudentAccessManager';

const baseUrl = API_BASE_URL;

/**
 * Most students never upload a photo (currently 20 of 22), and profile_img comes
 * back as null for them — rendering <img src={null}> produced a broken-image icon.
 * Fall back to initials, and also swap to initials if a real image 404s.
 */
const StudentAvatar = ({ src, name }) => {
    const [failed, setFailed] = useState(false);
    const initials = (name || '?')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(w => w[0])
        .join('')
        .toUpperCase();

    if (src && !failed) {
        return (
            <img
                className='imgmeet'
                src={src}
                alt={name || 'Student'}
                onError={() => setFailed(true)}
                style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }}
            />
        );
    }

    return (
        <span
            title={name || 'Student'}
            aria-label={name || 'Student'}
            style={{
                width: 44, height: 44, borderRadius: '50%',
                background: '#101C2C', color: '#C9A66B',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 15, letterSpacing: 0.5, userSelect: 'none',
            }}
        >
            {initials}
        </span>
    );
};

const EnrolledStudents = () => {

    useEffect(()=>{
        document.title='LMS | Enrolled Students'
      }, []);

      const [studentData, setStudentData]=useState([]);
      const [courseData, setCourseData]=useState([]);
      const [accessStudent, setAccessStudent]=useState(null);
      // Without these, a course with no enrolments — or a failed request — rendered an
      // empty table with no explanation, which reads as "the Manage Access button
      // disappeared".
      const [loading, setLoading]=useState(true);
      const [loadError, setLoadError]=useState(null);
      const teacherId=localStorage.getItem('teacherId');
      let {course_id}=useParams();

      useEffect(()=>{
        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        // NOTE: a try/catch around axios does not catch a rejected promise — the old
        // code swallowed failures and left the table blank. Use .catch().
        axios.get(baseUrl+'/fetch-enrolled-students/'+course_id)
            .then((res)=>{
                if (cancelled) return;
                setStudentData(Array.isArray(res.data) ? res.data : []);
            })
            .catch((error)=>{
                if (cancelled) return;
                console.log('Error fetching enrolled students:', error);
                setLoadError('Could not load enrolled students. Please refresh the page.');
            })
            .finally(()=>{
                if (!cancelled) setLoading(false);
            });
        return ()=>{ cancelled = true; };
      },[course_id]);

      useEffect(()=>{
        let cancelled = false;
        axios.get(baseUrl+'/teacher-course/'+teacherId)
            .then((res)=>{
                if (!cancelled) setCourseData(Array.isArray(res.data) ? res.data : []);
            })
            .catch((error)=>{ console.log('Error fetching teacher courses:', error); });
        return ()=>{ cancelled = true; };
      },[teacherId]);

      const currentCourse = courseData.find(c => String(c.id) === String(course_id));

  return (
    <>
        <div className='card'>
                    <h5 className='card-header d-flex justify-content-between align-items-center flex-wrap gap-2'>
                        <span>
                            <i className="bi bi-people-fill"></i> Enrolled Students
                            {currentCourse && <span className='text-muted fw-normal'> — {currentCourse.title}</span>}
                        </span>
                        {/* Course Management is the course list the teacher sidebar actually
                            links to; /teacher/my-courses is not in the menu. */}
                        <Link to='/teacher/course-management' className='btn btn-sm btn-outline-secondary'>
                            <i className="bi bi-arrow-left"></i> Back to Course Management
                        </Link>
                    </h5>
                    <div className='card-body table-responsive'>
                        {loading && (
                            <div className='text-center text-muted py-4'>
                                <div className='spinner-border spinner-border-sm me-2' role='status'></div>
                                Loading enrolled students…
                            </div>
                        )}

                        {!loading && loadError && (
                            <div className='alert alert-danger mb-0'>{loadError}</div>
                        )}

                        {!loading && !loadError && studentData.length === 0 && (
                            <div className='text-center py-4'>
                                <div style={{ fontSize: 40, color: '#C9A66B' }}>
                                    <i className="bi bi-person-plus"></i>
                                </div>
                                <h6 className='mt-3 mb-1'>No students are enrolled in this course yet</h6>
                                <p className='text-muted mb-0'>
                                    Access controls appear here as soon as a student enrols. You can then set
                                    each student to <strong>Auto</strong> (one module per week),
                                    <strong> Unlocked</strong> (full self-paced access) or
                                    <strong> Locked</strong> (open modules and lessons individually).
                                </p>
                            </div>
                        )}

                        {!loading && !loadError && studentData.length > 0 && (
                        <table className='table table-striped table-hover'>
                            <thead>
                                <tr>
                                    <th className='text-center'>Profile</th>
                                    <th className='text-center'>Name</th>
                                    <th className='text-center'>Email</th>
                                    <th className='text-center'>Interest</th>
                                    <th className='text-center'>Access</th>
                                </tr>
                            </thead>
                            <tbody>
                                {studentData.map((row,index) =>
                                    <tr key={index}>
                                    <td className='text-center' scope="row"><StudentAvatar src={row.student.profile_img} name={row.student.fullname} /></td>
                                    <td className='text-center' scope="row">{row.student.fullname}</td>
                                    <td className='text-center w-auto' scope="row">{row.student.email}</td>
                                    <td className='text-center' scope="row">{row.student.interseted_categories}</td>
                                    <td className='text-center' scope="row">
                                        <button className="btn btn-sm" style={{ background:'#101C2C', color:'#fff', border:'none', whiteSpace:'nowrap' }}
                                            onClick={() => setAccessStudent(row.student)}>
                                            <i className="bi bi-sliders me-1"></i> Manage Access
                                        </button>
                                    </td>
                                </tr>
                                )}
                            </tbody>
                        </table>
                        )}
                    </div>
                </div>

        {accessStudent && (
            <StudentAccessManager
                studentId={accessStudent.id}
                studentName={accessStudent.fullname}
                courseId={course_id}
                requesterType="teacher"
                requesterId={teacherId}
                onClose={() => setAccessStudent(null)}
            />
        )}
    </>
  )
}

export default EnrolledStudents
